import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import {
	resetUpdateWorkerTypeHours,
	updateWorkerTypeHours,
} from "../../../../store/gat-admin-slice";
import { WorkerTypeHours } from "../../../../store/gat-admin-slice-types";

const MAX_HOURS = 168;
export const useWeeklyHourLimitController = (
	workerTypeHour: WorkerTypeHours
) => {
	const dispatch = useDispatch();
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [validationError, setValidationError] = useState<string>("");
	const isSaving = useRef(false);
	// Status is shared by every row, so track which row dispatched the save
	const hasPendingSave = useRef(false);
	const rowRef = useRef<HTMLDivElement>(null);
	const [workerTypeLimit, setWorkerTypeLimit] = useState(workerTypeHour);

	const { updateWorkerTypeHoursStatus } = useGatAdminConfigData();

	const isUnchanged =
		workerTypeLimit.maxHours === workerTypeHour.maxHours &&
		(workerTypeLimit.minHours ?? 0) === (workerTypeHour.minHours ?? 0);

	const handleHoursChange = (value: number, type: "min" | "max") => {
		const sanitizedValue = isNaN(value)
			? 0
			: Math.max(0, Math.min(value, MAX_HOURS));
		setValidationError("");
		setWorkerTypeLimit((prev) => ({
			...prev,
			[type === "min" ? "minHours" : "maxHours"]: sanitizedValue,
		}));
	};

	// Returns false when the values are invalid and the row should stay open
	const commit = () => {
		if (isUnchanged) {
			setIsFocused(false);
			return true;
		}
		const minHours = workerTypeLimit.minHours ?? 0;
		// Max hours of 0 means unlimited
		if (workerTypeLimit.maxHours !== 0 && minHours > workerTypeLimit.maxHours) {
			setValidationError("Min. cannot exceed max.");
			return false;
		}
		hasPendingSave.current = true;
		setIsFocused(false);
		dispatch(updateWorkerTypeHours([{ ...workerTypeLimit, minHours }]));
		return true;
	};

	// Drop focus from the row without the blur handler saving a second time
	const releaseFocus = () => {
		const active = document.activeElement as HTMLElement | null;
		if (!active || !rowRef.current?.contains(active)) return;
		isSaving.current = true;
		active.blur();
		isSaving.current = false;
	};

	const handleSave = () => {
		if (commit()) releaseFocus();
	};

	const handleCancel = () => {
		setWorkerTypeLimit((prev) => ({
			...prev,
			maxHours: workerTypeHour.maxHours,
			minHours: workerTypeHour.minHours,
		}));
		setValidationError("");
		setIsFocused(false);
		releaseFocus();
	};

	// Keeps focus in the input so blur does not fire before the click
	const handleActionMouseDown = (event: React.MouseEvent) => {
		event.preventDefault();
	};

	const handleBlur = (event: React.FocusEvent) => {
		if (isSaving.current) return;
		// Moving between the min and max fields of the same row is not a save
		if (rowRef.current?.contains(event.relatedTarget as Node | null)) return;
		commit();
	};

	const resetUpdateWorkerType = () => {
		dispatch(resetUpdateWorkerTypeHours());
	};

	useEffect(() => {
		if (workerTypeHour) {
			setWorkerTypeLimit(workerTypeHour);
		}
	}, [workerTypeHour]);

	// A rejected save leaves the store untouched, so show the saved values again
	useEffect(() => {
		if (!hasPendingSave.current) return;
		if (updateWorkerTypeHoursStatus === "error") {
			hasPendingSave.current = false;
			setWorkerTypeLimit(workerTypeHour);
		} else if (updateWorkerTypeHoursStatus === "fulfilled") {
			hasPendingSave.current = false;
		}
	}, [updateWorkerTypeHoursStatus, workerTypeHour]);

	return [
		{
			workerTypeLimit,
			isFocused,
			validationError,
			rowRef,
			updateWorkerTypeHoursStatus,
		},
		{
			setIsFocused,
			handleBlur,
			handleHoursChange,
			handleSave,
			handleCancel,
			handleActionMouseDown,
			resetUpdateWorkerType,
		},
	] as const;
};
